import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { usePageMeta } from "../lib/seo";
import { visitorId } from "./PublicFeedback";

function StaffCard({ e, onPick }) {
  return (
    <button onClick={() => onPick(e)} data-testid={`pick-employee-${e.token}`}
      className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 text-left shadow-sm transition-transform hover:-translate-y-0.5 hover:border-indigo-300 active:scale-[0.98]">
      {e.photo ? <img src={e.photo} alt="" className="h-14 w-14 flex-shrink-0 rounded-full object-cover" />
        : <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 font-head text-xl font-bold text-indigo-700">{(e.first_name || "?")[0]}</span>}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-head text-base font-bold text-slate-900">{e.name}</span>
        {e.title && <span className="block truncate text-xs text-slate-500">{e.title}</span>}
      </span>
      <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-indigo-500" />
    </button>
  );
}

export default function PublicPoint() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  usePageMeta("Who served you? | Servantage", "/", true);
  const src = new URLSearchParams(window.location.search).get("s") === "nfc" ? "nfc" : "qr";

  useEffect(() => {
    api.get(`/public/p/${token}?v=${encodeURIComponent(visitorId() || "")}`).then((r) => setD(r.data)).catch((e) => setD({ error: e?.response?.data?.detail || "This QR code is not valid." }));
  }, [token]);

  const list = useMemo(() => {
    const filtered = (d?.employees || []).filter((e) => !q || e.name.toLowerCase().includes(q.toLowerCase()));
    return filtered.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
  }, [d, q]);
  const pick = (e) => navigate(`/r/${e.token}?s=${src}&p=${token}`);

  if (!d) return <div className="flex min-h-screen items-center justify-center text-slate-400">Loading…</div>;
  if (d.error) return <div className="flex min-h-screen items-center justify-center px-6 text-center text-slate-500" data-testid="point-error">{d.error}</div>;
  return (
    <div className="min-h-screen bg-slate-50 px-5 pb-12 pt-8" data-testid="public-point">
      <div className="mx-auto max-w-md">
        <div className="flex items-center justify-center gap-3">
          {d.organization_logo && <img src={d.organization_logo} alt="" className="h-12 w-12 rounded-xl object-contain" />}
          <p className="font-head text-xl font-bold text-slate-900">{d.organization_name}</p>
        </div>
        <p className="mt-1 text-center text-xs font-bold uppercase tracking-[0.18em] text-slate-400" data-testid="point-place">{d.location_name} · {d.point_name}</p>
        <h1 className="mt-6 text-center font-head text-3xl font-bold text-slate-900">Who served you today?</h1>
        <p className="mt-2 text-center text-sm text-slate-500">Tap their name to rate your experience. It takes 20 seconds.</p>
        {d.employees.length > 1 && (
          <div className="relative mt-5"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee by name" data-testid="point-search"
              className="h-10 w-full rounded-full border border-slate-200 bg-white pl-9 pr-4 text-sm focus:border-indigo-400 focus:outline-none" /></div>
        )}
        <div className="mt-5 grid gap-3" data-testid="point-staff-list">
          {list.length === 0 ? <p className="py-8 text-center text-sm text-slate-400">No staff found.</p> : list.map((e) => <StaffCard key={e.token} e={e} onPick={pick} />)}
        </div>
        <p className="mt-10 text-center text-xs font-bold text-slate-700">Solution brought to you by Ser<span className="text-[#4338CA]">vantage</span></p>
      </div>
    </div>
  );
}
